import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import UserPatternSettingUpdate from "./_111_user_pattern_setting_update.js";
import { useDateRange } from "./dateRangeContext.js";
import { logEvent } from "./eventLogger.js";
import { getCachedPatternData, setCachedPatternData } from "./patterns/cacheHelper.js";
import "./styles.css";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { formatDateMMDDYY } from "./patterns/helpers.js";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

const MODULE_NAME = "_96_multi_day_trade";
const screen = "pg_patterns";

// --- Gear Icon Unicode ---
const GearIcon = ({ size = 24, color = "#fff" }) => (
    <span
        style={{
            fontSize: size,
            color,
            verticalAlign: "middle",
            cursor: "pointer",
            userSelect: "none",
            lineHeight: 1,
            display: "inline-block",
        }}
        title="Settings"
        aria-label="Settings"
    >
        &#9881;
    </span>
);

const Multi_day_trade_pattern = ({ userId: propUserId }) => {
    const { windowDays } = useDateRange();
    
    // Derived userId with fallback
    const storedId = sessionStorage.getItem("user_id");
    const userId = propUserId || (storedId && !isNaN(Number(storedId)) ? Number(storedId) : 1);

    const [date, setDate] = useState("");
    const [tooltip, setTooltip] = useState("");
    const [multiDayPattern, setMultiDayPattern] = useState(null);
    const [totalPL, setTotalPL] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [chartMode, setChartMode] = useState("cumulative");
    const [excludeETF, setExcludeETF] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [patternId, setPatternId] = useState(null);
    const [patternConfig, setPatternConfig] = useState(null);
    const [threshold1Used, setThreshold1Used] = useState(null);
    const [defaultLookbackWindowUsed, setDefaultLookbackWindowUsed] = useState(null);
    const [fromCache, setFromCache] = useState(false);
    const chartContainerRef = useRef(null);

    // Helper to parse windowDays to a valid lookback number
    function getLookbackDays(val) {
        if (!val) return 30;
        if (!isNaN(Number(val))) return Number(val);
        if (val === "YTD") return 365;
        return 30;
    }

    // ✅ UPDATED: Handle flat structure returned from backend and userKey for decryption
    const fetchPatternData = async (days, exclude_etf) => {
        setLoading(true);
        
        try {
            // 🔓 NEW: Retrieve userKey from sessionStorage for decryption
            const userKey = sessionStorage.getItem("userKey");
            const fetchParams = { 
                lookback_days: days,
                exclude_etf: exclude_etf
            };

            // Inject userKey if Privacy Mode is active
            if (userKey) {
                fetchParams.userKey = userKey;
            }

            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/_96_multi_day_trade_pattern`,
                {
                    params: fetchParams,
                    withCredentials: true,
                }
            );
            
            // ✅ UPDATED: Backend now returns flat structure (no wrapper)
            const data = res.data;
            setDate(data.date);
            setTooltip(data.tooltip || "");
            setMultiDayPattern(data);
            setTotalPL(data.total_pl);

            const numericPatternId = data.pattern_id;
            setPatternId(numericPatternId);
            
            setPatternConfig(data.config || {
                count_threshold: {
                    emerging: { value: 5, label: "Emerging Pattern", description: "Your recent activity shows early signs of concentrating positions across multi-day trades." },
                    confirmed: { value: 10, label: "Confirmed Pattern", description: "Your trading behavior reflects a recurring tendency to concentrate positions in multi-day trades." },
                    strong: { value: 15, label: "Strong Pattern", description: "Your trading style shows a well established inclination toward concentrated positions in multi-day trades." }
                },
                threshold_definition: {
                    threshold_1: { value: 0.25, label: "Cash % threshold", description: "Percentage of available cash in decimal." }
                }
            });

            setThreshold1Used(data.threshold_1_used);
            setDefaultLookbackWindowUsed(data.default_lookback_window_used);

            if (numericPatternId) {
                await setCachedPatternData(
                    `pattern_${numericPatternId}`,
                    days,
                    data,
                    { exclude_etf: exclude_etf }
                );
            }

            console.log("Pattern ID:", numericPatternId, "Config:", data.config);
            setFromCache(false);
        } catch (err) {
            console.error("Error fetching pattern data:", err);
            
            if (patternId) {
                const cachedData = await getCachedPatternData(
                    `pattern_${patternId}`,
                    days,
                    { exclude_etf: exclude_etf }
                );
                if (cachedData) {
                    setDate(cachedData.date);
                    setTooltip(cachedData.tooltip || "");
                    setMultiDayPattern(cachedData);
                    setTotalPL(cachedData.total_pl);
                    setThreshold1Used(cachedData.threshold_1_used);
                    setDefaultLookbackWindowUsed(cachedData.default_lookback_window_used);
                    setPatternConfig(cachedData.config || {});
                    setFromCache(true);
                    setLoading(false);
                    return;
                }
            }
            
            setTooltip("Error fetching data.");
            setMultiDayPattern(null);
            setTotalPL(null);
            setPatternId(null);
        }
        
        setLoading(false);
    };

    useEffect(() => {
        const lookback = getLookbackDays(windowDays);
        if (lookback) {
            fetchPatternData(lookback, excludeETF);
        }
        // eslint-disable-next-line
    }, [windowDays, excludeETF]);

    // Tooltip icon
    const tooltipIcon = tooltip ? (
        <span
            className="tooltip-icon"
            style={{
                marginLeft: 6,
                verticalAlign: "middle",
                cursor: "pointer",
                fontSize: "1.1em",
            }}
            title={tooltip}
        >
            🛈
        </span>
    ) : null;

    // Chart data preparation
    const getChartData = () => {
        if (!multiDayPattern || !Array.isArray(multiDayPattern.time_evolution))
            return [];
        if (chartMode === "cumulative") {
            return multiDayPattern.time_evolution;
        }
        if (chartMode === "rolling") {
            const arr = multiDayPattern.time_evolution;
            const window = 5;
            return arr.map((d, i) => {
                const slice = arr.slice(Math.max(0, i - window + 1), i + 1);
                const avgPL =
                    slice.reduce((sum, x) => sum + (x.daily_pl || 0), 0) / slice.length;
                const avgWin =
                    (slice.reduce((sum, x) => sum + (x.daily_wins || 0), 0) /
                        (slice.reduce((sum, x) => sum + (x.daily_trades || 0), 0) || 1)) *
                    100;
                const avgFreq =
                    slice.reduce((sum, x) => sum + (x.frequency || 0), 0) / slice.length;
                return {
                    ...d,
                    rolling_pl: avgPL,
                    rolling_win_rate: avgWin,
                    rolling_frequency: avgFreq,
                };
            });
        }
        if (chartMode === "scatter" && Array.isArray(multiDayPattern.pl_matches)) {
            return multiDayPattern.pl_matches.map((d, i) => ({
                ...d,
                index: i + 1,
            }));
        }
        return [];
    };

    // Helper to get chart labels in MM/DD/YY format
    const getChartLabels = (data) =>
        data.map((d) => formatDateMMDDYY(d.date || d.closing_date));

    // Prepare Chart.js datasets
    const buildChartData = () => {
        const data = getChartData();
        if (!data.length) return { labels: [], datasets: [] };

        if (chartMode === "scatter") {
            return {
                labels: getChartLabels(data),
                datasets: [
                    {
                        type: "scatter",
                        label: "Trade P/L",
                        data: data.map((d, idx) => ({
                            x: idx,
                            y: d.pl,
                        })),
                        backgroundColor: data.map((d) =>
                            d.pl >= 0 ? "rgba(76, 175, 80, 0.6)" : "rgba(244, 67, 54, 0.6)"
                        ),
                        pointRadius: 6,
                        yAxisID: "y",
                    },
                ],
            };
        }

        const plKey = chartMode === "rolling" ? "rolling_pl" : "cumulative_pl";
        const winKey = chartMode === "rolling" ? "rolling_win_rate" : "cumulative_win_rate";
        const freqKey = chartMode === "rolling" ? "rolling_frequency" : "frequency";
        const winsKey = chartMode === "rolling" ? "rolling_wins" : "daily_wins";

        // Calculate wins and losses for each data point
        const wins = data.map(d => d[winsKey] || 0);
        const freq = data.map(d => d[freqKey] || 0);
        const losses = freq.map((f, i) => Math.max(0, f - wins[i]));

        return {
            labels: getChartLabels(data),
            datasets: [
                {
                    type: "line",
                    label: "P/L",
                    data: data.map((d) => d[plKey]),
                    borderColor: "#1976d2",
                    backgroundColor: "rgba(25, 118, 210, 0.1)",
                    borderWidth: 3,
                    pointRadius: 3,
                    fill: true,
                    tension: 0.3,
                    yAxisID: "y",
                },
                {
                    type: "line",
                    label: "Win Rate %",
                    data: data.map((d) => d[winKey]),
                    borderColor: "#4caf50",
                    backgroundColor: "rgba(76, 175, 80, 0.1)",
                    borderWidth: 2,
                    pointRadius: 2,
                    fill: true,
                    tension: 0.3,
                    yAxisID: "y1",
                },
                {
                    type: "bar",
                    label: "Wins",
                    data: wins,
                    backgroundColor: "rgba(76, 175, 80, 0.6)",
                    borderColor: "#4caf50",
                    borderWidth: 1,
                    yAxisID: "y2",
                    stack: "frequency",
                },
                {
                    type: "bar",
                    label: "Losses",
                    data: losses,
                    backgroundColor: "rgba(244, 67, 54, 0.6)",
                    borderColor: "#F44336",
                    borderWidth: 1,
                    yAxisID: "y2",
                    stack: "frequency",
                },
            ],
        };
    };

    const chartData = buildChartData();
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true, position: "top" },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        if (chartMode === "scatter" && multiDayPattern?.pl_matches) {
                            const trade = multiDayPattern.pl_matches[context.dataIndex];
                            const matchingGroup = trade.matching_group ? ` [${trade.matching_group}]` : "";
                            return `${trade.symbol}${matchingGroup}: $${trade.pl.toLocaleString()}`;
                        }
                        return `${context.dataset.label}: ${context.parsed.y}`;
                    }
                }
            }
        },
        scales: {
            y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "P/L ($)" },
                ticks: { color: "#fff" },
                grid: { color: "#444" },
            },
            y1: {
                display: chartMode !== "scatter",
                type: "linear",
                position: "right",
                title: { display: true, text: "Win Rate (%)" },
                ticks: { color: "#fff" },
                grid: { drawOnChartArea: false },
            },
            y2: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Frequency" },
                ticks: { color: "#fff" },
                grid: { drawOnChartArea: false },
                offset: true,
            },
            x: {
                ticks: { 
                    color: "#fff",
                    callback: function(value, index) {
                        return chartData.labels[index] || value;
                    }
                },
                grid: { color: "#444" },
            },
        },
    };

    // --- Settings Modal ---
    const renderSettingsModal = () => {
        if (!showSettings || !patternId) return null;

        return (
            <>
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(24,26,37,0.5)",
                        backdropFilter: "blur(6px)",
                        zIndex: 9998,
                    }}
                    onClick={() => setShowSettings(false)}
                />
                <div
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 9999,
                        minWidth: 600,
                        minHeight: 420,
                        background: "#181A25",
                        borderRadius: 12,
                        boxShadow: "0 2px 8px rgba(0,255,255,0.08)",
                        padding: 0,
                    }}
                >
                    <UserPatternSettingUpdate
                        patternId={patternId}
                        countThreshold={patternConfig?.count_threshold}
                        thresholdDefinition={patternConfig?.threshold_definition}
                    />
                    <button
                        onClick={() => setShowSettings(false)}
                        style={{
                            position: "absolute",
                            top: 12,
                            right: 18,
                            background: "none",
                            border: "none",
                            color: "#fff",
                            fontSize: 22,
                            cursor: "pointer",
                        }}
                        title="Close"
                    >
                        ✕
                    </button>
                </div>
            </>
        );
    };

    // Pattern label/meaning and details
    const renderPatternMessages = () => {
        if (!multiDayPattern) {
            return <span>No data available.</span>;
        }

        let label = multiDayPattern.pattern_label;
        let description = multiDayPattern.pattern_meaning;

        // Fallback to config thresholds if label/meaning not set
        if (
            (!label || !description) &&
            multiDayPattern.config &&
            multiDayPattern.config.count_threshold
        ) {
            const thresholds = multiDayPattern.config.count_threshold;
            if (thresholds.emerging) {
                label = thresholds.emerging.label;
                description = thresholds.emerging.description;
            }
        }

        // Group by symbol and extract matching group info
        const symbolMap = {};
        if (Array.isArray(multiDayPattern.pl_matches)) {
            multiDayPattern.pl_matches.forEach((item) => {
                if (!symbolMap[item.symbol]) {
                    symbolMap[item.symbol] = [];
                }
                symbolMap[item.symbol].push({
                    pl: item.pl,
                    matching_group: item.matching_group
                });
            });
        }

        const groupedDetails = Object.entries(symbolMap)
            .slice(0, 10)
            .map(([symbol, trades]) => ({ 
                symbol, 
                count: trades.length,
                groups: trades.map(t => t.matching_group).join(", ")
            }));

        // Determine which threshold to display (database or config)
        const displayThreshold = threshold1Used !== null && threshold1Used !== undefined
            ? threshold1Used
            : (multiDayPattern.config?.pattern_threshold_percentage || multiDayPattern.threshold_1 || 0.25);

        const moreDetails = (
            <div style={{ margin: "8px 0" }} data-analytics={MODULE_NAME}>
                <a
                    href="#"
                    style={{
                        color: "#4fa3f7",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "0.97em",
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        setShowDetails((prev) => !prev);
                    }}
                >
                    {showDetails ? "Hide details" : "More details"}
                </a>
                {showDetails && (
                    <div
                        style={{
                            marginTop: 6,
                            color: "#aaa",
                            fontSize: "0.97em",
                        }}
                    >
                        <div>
                            Threshold (from {threshold1Used !== null ? "database" : "config"}):
                            <b>
                                {" "}{(parseFloat(displayThreshold) * 100).toFixed(2)}%
                            </b>
                        </div>
                        <div>
                            Lookback window:
                            <b>
                                {" "}{defaultLookbackWindowUsed || multiDayPattern.lookback_days || 30} days
                            </b>
                        </div>
                        <div>
                            Pattern matches: <b>{multiDayPattern.pattern_matches || 0}</b>
                        </div>
                        {multiDayPattern.recent_events_30d !== undefined && (
                            <div>
                                Recent events (30 days): <b>{multiDayPattern.recent_events_30d}</b>
                            </div>
                        )}
                        {/* ✅ NEW: Show cache status */}
                        {fromCache && (
                            <div style={{ marginTop: 6, fontSize: "0.9em", color: "#888" }}>
                                📦 Data loaded from database cache (4 hour TTL)
                            </div>
                        )}
                    </div>
                )}
            </div>
        );

        return (
            <div style={{ marginBottom: 8 }}>
                <span>
                    {label || "Emerging Pattern"}: {description}
                </span>
                {moreDetails}
                {groupedDetails.length > 0 && (
                    <div style={{ marginTop: 8, color: "#ccc", fontSize: "0.97em" }}>
                        {groupedDetails.map((item, idx) => (
                            <div key={idx}>
                                <b>{item.symbol}:</b> {item.count} multi-day concentration event(s) [groups: {item.groups}]
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Chart mode toggles
    const renderChartToggles = () => (
        <div style={{ display: "flex", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
            {["cumulative", "rolling", "scatter"].map((mode) => {
                const buttonLabels = {
                    cumulative: "Cumulative",
                    rolling: "Rolling Avg",
                    scatter: "Trade Scatter"
                };
                const buttonLabel = buttonLabels[mode];
                const subcomponentLabel = mode === "cumulative" 
                    ? "Cumulative Chart" 
                    : mode === "rolling" 
                    ? "Rolling Avg Chart" 
                    : "Trade Scatter Chart";

                return (
                    <div key={mode} data-analytics={MODULE_NAME}>
                        <button
                            onClick={() => {
                                logEvent({
                                    event_type: "button_click",
                                    user_id: userId,
                                    module_name: MODULE_NAME,
                                    subcomponent: subcomponentLabel,
                                    screen: window.location.pathname,
                                    metadata: {
                                        button_link_value: buttonLabel
                                    }
                                });
                                setChartMode(mode);
                            }}
                            className={`btn ${chartMode === mode ? "btn-selected" : ""}`}
                            style={{
                                padding: "6px 14px",
                                borderRadius: 6,
                                border: "1px solid #888",
                                background: chartMode === mode ? "#1976d2" : "#f5f5f5",
                                color: chartMode === mode ? "#fff" : "#222",
                                fontWeight: 500,
                                cursor: "pointer",
                                transition: "background 0.2s",
                                fontSize: "0.98em",
                            }}
                        >
                            {buttonLabel}
                        </button>
                    </div>
                );
            })}
        </div>
    );

    // ETF toggle
    const renderExcludeETFToggle = () => {
        const toggleStyle = {
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            userSelect: "none",
        };
        const checkboxStyle = {
            width: 38,
            height: 22,
            position: "relative",
            display: "inline-block",
            background: excludeETF ? "#1976d2" : "#ccc",
            borderRadius: 12,
            transition: "background 0.2s",
            verticalAlign: "middle",
        };
        const sliderStyle = {
            position: "absolute",
            left: excludeETF ? 18 : 2,
            top: 2,
            width: 18,
            height: 18,
            background: "#fff",
            borderRadius: "50%",
            boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
            transition: "left 0.2s",
        };

        return (
            <span style={toggleStyle} data-analytics={MODULE_NAME}>
                <span style={{ fontSize: "0.98em", color: "#e0b400" }}>Exclude ETFs</span>
                <span
                    style={checkboxStyle}
                    onClick={() => {
                        const newVal = !excludeETF;
                        logEvent({
                            event_type: "toggle_click",
                            user_id: userId,
                            module_name: MODULE_NAME,
                            subcomponent: "Exclude ETF Toggle",
                            screen: window.location.pathname,
                            metadata: {
                                name: "exclude_etf",
                                enabled: newVal ? 1 : 0,
                                button_link_value: newVal ? "Enabled" : "Disabled"
                            }
                        });
                        setExcludeETF(newVal);
                    }}
                    tabIndex={0}
                    role="checkbox"
                    aria-checked={excludeETF}
                >
                    <span style={sliderStyle}></span>
                </span>
            </span>
        );
    };

    return (
        <div
            ref={chartContainerRef}
            style={{
                width: "100%",
                background: "none",
                borderRadius: 0,
                padding: 0,
                margin: 0,
                maxWidth: "100%",
                position: "relative",
            }}
        >
            <h3
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: "1.25em",
                    margin: "0 0 8px 0",
                    color: "#fff",
                }}
            >
                {multiDayPattern?.pattern_name || "Multi-Day Concentration Pattern"}{" "}
                {tooltipIcon}
                <span
                    style={{ marginLeft: 8 }}
                    title="Edit pattern settings"
                    onClick={() => {
                        logEvent({
                            event_type: "button_click",
                            user_id: userId,
                            module_name: MODULE_NAME,
                            subcomponent: "Settings Gear",
                            screen: window.location.pathname,
                            metadata: { button_link_value: "Settings Gear" }
                        });
                        setShowSettings(true);
                    }}
                >
                    <GearIcon size={24} color="#fff" />
                </span>
            </h3>
            {renderSettingsModal()}
            <div style={{ minHeight: 32, fontSize: "1.05em", marginBottom: 16 }}>
                {loading ? (
                    <span>Loading...</span>
                ) : (
                    <>
                        {/* Header Row: Date and ETF Toggle */}
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: 12,
                                flexWrap: "wrap",
                                gap: 12,
                            }}
                        >
                            <span style={{ color: "#888", fontSize: "0.95em" }}>
                                
                            </span>
                            {renderExcludeETFToggle()}
                        </div>

                        {/* P/L Display */}
                        {typeof totalPL === "number" && (
                            <div
                                style={{
                                    marginTop: 6,
                                    color: "#e0b400",
                                    fontSize: "1.1em",
                                    marginBottom: 12,
                                }}
                            >
                                <b>P/L:</b>{" "}
                                <span
                                    style={{
                                        color: totalPL >= 0 ? "#4caf50" : "#e53935",
                                    }}
                                >
                                    ${totalPL.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </span>
                            </div>
                        )}

                        {/* Pattern Messages */}
                        <div style={{ marginBottom: 12 }}>{renderPatternMessages()}</div>

                        {/* Chart Section */}
                        <div style={{ marginTop: 16 }}>
                            {renderChartToggles()}
                            <div
                                style={{
                                    position: "relative",
                                    width: "100%",
                                    height: 400,
                                    maxWidth: "100%",
                                }}
                            >
                                {chartData.datasets.length > 0 ? (
                                    <Chart
                                        key={chartMode}
                                        type={chartMode === "scatter" ? "scatter" : "bar"}
                                        data={chartData}
                                        options={chartOptions}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            color: "#888",
                                            padding: "20px",
                                            textAlign: "center",
                                        }}
                                    >
                                        No chart data available for this period.
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Multi_day_trade_pattern;